import { useEffect, useRef } from "react";
import { Head, Link, router, useForm, usePage } from "@inertiajs/react";
import { CheckCircle2, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import PageContainer from "@/components/marketing/PageContainer";
import SectionHeading from "@/components/marketing/SectionHeading";

export default function Contact({ topic = "general", errors: serverErrors = {}, values = {}, sent = false }) {
  const { routes } = usePage().props;
  const { data, setData, post, processing, errors: formErrors, reset, setError } = useForm({ name: values.name || "", email: values.email || "", topic, message: values.message || "", xq_ref: "" });
  const errors = { ...serverErrors, ...formErrors };
  const successRef = useRef(null);

  useEffect(() => {
    if (!sent || !successRef.current) return;
    // Wait until Inertia has finished restoring the page scroll position.
    const frame = requestAnimationFrame(() => {
      successRef.current?.focus({ preventScroll: true });
      successRef.current?.scrollIntoView({ block: "center" });
    });
    return () => cancelAnimationFrame(frame);
  }, [sent]);
  useEffect(() => router.on("invalid", (event) => {
    if (event.detail.response.status === 429) {
      event.preventDefault();
      setError("delivery", "You’ve sent several messages recently. Please try again in an hour.");
    }
  }), [setError]);
  const submit = (event) => {
    event.preventDefault();
    post(routes.contact, { preserveScroll: "errors", onSuccess: (page) => { if (page.props.sent) reset("name", "email", "message"); } });
  };

  return (
    <MarketingLayout>
      <Head title={sent ? "Message sent" : "Contact"}>
        {sent && <meta name="robots" content="noindex" />}
      </Head>
      <PageContainer width="narrow" className="pt-10 md:pt-14 pb-12 md:pb-16">
        <SectionHeading as="h1" flourish="get in touch" title="We're here to help."
          subhead="Questions about early adopter pricing, your campaign, or PagePledge? Send us a message. A real person will get back to you, usually within one business day." />
        <div className="mt-8 mx-auto max-w-xl rounded-xl border border-border bg-card p-5 sm:p-8 shadow-sm">
          {sent ? (
            <div ref={successRef} role="status" tabIndex={-1} className="space-y-3 rounded-lg bg-success/10 p-6 text-center focus:outline-none">
              <CheckCircle2 className="h-9 w-9 text-success mx-auto" aria-hidden="true" />
              <h2 className="text-2xl">Message sent.</h2>
              <p className="text-muted-foreground">Thanks for getting in touch. We'll reply to the email address you provided.</p>
              <Button variant="outline" asChild><Link href={routes.contact}>Send another message</Link></Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              {errors.delivery && <p role="alert" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">{errors.delivery}</p>}
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Your name" name="name" error={errors.name}>
                  <Input id="name" name="name" autoComplete="name" required maxLength={100} value={data.name} onChange={e => setData("name", e.target.value)} aria-invalid={!!errors.name} aria-describedby={errors.name ? "name-error" : undefined} />
                </Field>
                <Field label="Email address" name="email" error={errors.email}>
                  <Input id="email" name="email" type="email" autoComplete="email" required maxLength={254} value={data.email} onChange={e => setData("email", e.target.value)} aria-invalid={!!errors.email} aria-describedby={errors.email ? "email-error" : undefined} />
                </Field>
              </div>
              <Field label="What can we help with?" name="topic" error={errors.topic}>
                <select id="topic" name="topic" value={data.topic} onChange={e => setData("topic", e.target.value)} className="w-full h-11 rounded-lg border border-input bg-card px-3 text-base">
                  <option value="general">General questions & support</option>
                  <option value="privacy">Privacy & data requests</option>
                </select>
                <p className="text-xs text-muted-foreground">Sent to {data.topic === "privacy" ? "privacy@pagepledge.com" : "hello@pagepledge.com"}.</p>
              </Field>
              <Field label="Message" name="message" error={errors.message}>
                <textarea id="message" name="message" required maxLength={5000} rows={6} value={data.message} onChange={e => setData("message", e.target.value)} className="w-full rounded-lg border border-input bg-card px-4 py-3 text-base resize-y" aria-invalid={!!errors.message} aria-describedby={errors.message ? "message-error" : undefined} />
              </Field>
              <div hidden aria-hidden="true"><label htmlFor="xq_ref">Leave blank</label><input id="xq_ref" name="xq_ref" tabIndex={-1} autoComplete="off" value={data.xq_ref} onChange={e => setData("xq_ref", e.target.value)} /></div>
              <p className="text-xs leading-relaxed text-muted-foreground">We'll use your details to respond to your inquiry. <Link href={routes.privacy} className="text-primary hover:underline">Privacy policy</Link></p>
              <Button type="submit" disabled={processing} className="w-full sm:w-auto"><Send className="h-4 w-4" aria-hidden="true" />{processing ? "Sending…" : "Send message"}</Button>
            </form>
          )}
        </div>
      </PageContainer>
    </MarketingLayout>
  );
}

function Field({ label, name, error, children }) {
  return <div className="space-y-2"><Label htmlFor={name}>{label}</Label>{children}{error && <p id={`${name}-error`} role="alert" className="text-sm text-destructive">{error}</p>}</div>;
}
